"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const NAMESPACE = "new-sla-stereolithography";

const IMAGE_BASE = "/image/new-version/more/SLA/Application";

const CATEGORIES = [
	{ titleKey: "applicationsCategory1Title", descKey: "applicationsCategory1Desc" },
	{ titleKey: "applicationsCategory2Title", descKey: "applicationsCategory2Desc" },
	{ titleKey: "applicationsCategory3Title", descKey: "applicationsCategory3Desc" },
] as const;

const IMAGES = [
	{ src: `${IMAGE_BASE}/rapid-validation.webp`, labelKey: "applicationsImageLabel1" },
	{ src: `${IMAGE_BASE}/prototype-iteration.webp`, labelKey: "applicationsImageLabel2" },
	{ src: `${IMAGE_BASE}/functional-testing.webp`, labelKey: "applicationsImageLabel3" },
	{ src: `${IMAGE_BASE}/low-volume-production.webp`, labelKey: "applicationsImageLabel4" },
	{ src: `${IMAGE_BASE}/appearance-verification.webp`, labelKey: "applicationsImageLabel5" },
	{ src: `${IMAGE_BASE}/flexible-manufacturing.webp`, labelKey: "applicationsImageLabel6" },
] as const;

export default function SlaApplications() {
	const t = useTranslations(NAMESPACE);

	return (
		<section className="bg-[#ffffff] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
					<span className="mb-3 inline-block text-xl uppercase tracking-wider text-themePrimary400 max-md:text-lg">
						{t("applicationsEyebrow")}
					</span>
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("applicationsTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("applicationsDesc")}</p>
				</div>

				<div className="grid items-start gap-8 lg:grid-cols-[1fr_1.2fr] lg:gap-10 xl:gap-14">
					<div className="space-y-8">
						{CATEGORIES.map((cat) => (
							<div key={cat.titleKey}>
								<h3 className="mb-2 text-base font-semibold text-themePrimary400 sm:text-lg">
									{t(cat.titleKey)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t(cat.descKey)}</p>
							</div>
						))}
					</div>

					<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
						{IMAGES.map((image, index) => (
							<div
								key={image.labelKey}
								className={`${
									index === 5 ? "bg-[#88d43c]" : "bg-[#ededed]"
								} group flex flex-col justify-between rounded-xl p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:bg-themePrimary400 hover:shadow-[0_18px_40px_rgba(154,108,59,0.18)]`}
							>
								<div className="relative mb-4 h-16 w-16 overflow-hidden rounded-full ring-1 ring-black/5 transition-transform duration-300 group-hover:scale-105">
									<Image
										src={image.src}
										alt={t(image.labelKey)}
										fill
										className="object-cover transition-transform duration-300 group-hover:scale-110"
										sizes="80px"
									/>
								</div>
								<span className="self-end text-right text-sm font-semibold text-[#1f1f1f] transition-colors duration-300 group-hover:text-white">
									{t(image.labelKey)}
								</span>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
